import React, { useState, useEffect, useCallback } from 'react'
import {
  Search, Loader2, Merge, Undo2, Pencil, ChevronRight, ChevronDown,
  X, History, Layers, Lightbulb, ArrowRight, ArrowLeftRight, RefreshCw,
  ExternalLink, Home
} from 'lucide-react'
import { Card, Button, Badge } from '../ui'
import { useToast } from '../../contexts/ToastContext'
import {
  scoutCurationService, CurationBrand, BrandMember, MergePreview, MergeLogEntry, BrandSuggestion,
  SelfCandidate
} from '../../services/scoutCurationService'

/**
 * Brand curation: merge name variants of the same brand, rename the label, undo either.
 *
 * Used from two places with the same props — the Support area (acting for a customer) and the
 * customer's own brand screen. The API is customer-namespaced, so the only difference is where
 * customerId comes from.
 *
 * Design intent: the operator's scarce resource is judgment, not clicks. Every decision shows
 * the evidence behind it — post counts, EMV, and what a merge would actually move — before it
 * is confirmed.
 */

interface BrandCurationPanelProps {
  customerId: string
  /** Support sees the whole journal including automatic merges; customers see theirs too, but
   *  the heading language differs slightly. */
  context?: 'support' | 'customer'
}

const fmtMoney = (n: number): string =>
  `$${(n ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`

const fmtEMV = (n: number): string =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M`
  : n >= 1_000   ? `$${(n / 1_000).toFixed(1)}K`
                 : `$${(n || 0).toFixed(0)}`

const fmtDate = (s: string | null): string =>
  s ? new Date(s).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—'

/** Link to a post the brand was detected in, so a decision can be checked against the
 *  evidence instead of made from the name alone. Renders nothing when no post is recorded. */
const PostLink: React.FC<{ url: string | null; label?: string; className?: string }> = ({
  url, label = 'View post', className = '',
}) => {
  if (!url) return null
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className={`inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline ${className}`}
      title={url}
    >
      <ExternalLink className="w-3 h-3" />{label}
    </a>
  )
}

const ORIGIN_VARIANT: Record<string, 'default' | 'info' | 'warning'> = {
  auto: 'warning', support: 'info', customer: 'default'
}

type SortField = 'posts' | 'emv' | 'detections' | 'label' | 'last_seen_at' | 'member_count'
const PAGE_SIZE = 25

// Deterministic per-brand accent, matching how BrandValuePanel gives each brand an identity.
// Literal class strings so Tailwind keeps them.
const AVATARS = [
  'bg-blue-100 text-blue-700', 'bg-emerald-100 text-emerald-700', 'bg-amber-100 text-amber-700',
  'bg-purple-100 text-purple-700', 'bg-rose-100 text-rose-700', 'bg-cyan-100 text-cyan-700',
  'bg-indigo-100 text-indigo-700', 'bg-teal-100 text-teal-700',
]
const avatarFor = (name: string): string => {
  let h = 0
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0
  return AVATARS[h % AVATARS.length]
}

const BrandCurationPanel: React.FC<BrandCurationPanelProps> = ({ customerId, context = 'support' }) => {
  const [tab, setTab] = useState<'suggestions' | 'brands' | 'history'>('suggestions')

  // ── Suggestion queue ──
  const [suggestions, setSuggestions] = useState<BrandSuggestion[]>([])
  const [sugLoading, setSugLoading] = useState(false)
  const [sugTier, setSugTier] = useState<'' | 2 | 3>('')
  const [sugTotal, setSugTotal] = useState(0)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [generating, setGenerating] = useState(false)

  // ── Brand list ──
  const [brands, setBrands] = useState<CurationBrand[]>([])
  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState<SortField>('posts')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [page, setPage] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [loading, setLoading] = useState(false)
  // Errors and confirmations go through the app-wide toaster, not an inline banner.
  const { showSuccess, showError } = useToast()

  // ── Selection / merge ──
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [survivorId, setSurvivorId] = useState<string | null>(null)
  const [preview, setPreview] = useState<MergePreview | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const [merging, setMerging] = useState(false)

  // ── Expanded chains ──
  const [expanded, setExpanded] = useState<Record<string, BrandMember[] | 'loading'>>({})

  // ── Own brands ──
  const [selfCandidates, setSelfCandidates] = useState<SelfCandidate[]>([])
  const [showCandidates, setShowCandidates] = useState(false)
  const [selfBusyId, setSelfBusyId] = useState<string | null>(null)

  // ── Rename ──
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')

  // ── Journal ──
  const [log, setLog] = useState<MergeLogEntry[]>([])
  const [logLoading, setLogLoading] = useState(false)
  const [originFilter, setOriginFilter] = useState<'' | 'auto' | 'support' | 'customer'>('')

  const loadBrands = useCallback(async () => {
    if (!customerId) return
    setLoading(true)
    try {
      const res = await scoutCurationService.listBrands(customerId, {
        search, sortBy, order: sortOrder, page, limit: PAGE_SIZE
      })
      setBrands(res.data)
      setTotalItems(res.pagination.totalItems)
    } catch (e) {
      showError('Failed to Load Brands', e instanceof Error ? e.message : 'Please try again.')
    } finally {
      setLoading(false)
    }
  }, [customerId, search, sortBy, sortOrder, page])

  const loadSuggestions = useCallback(async () => {
    if (!customerId) return
    setSugLoading(true)
    try {
      const res = await scoutCurationService.getSuggestions(customerId, {
        status: 'pending', tier: sugTier || undefined, limit: 50
      })
      setSuggestions(res.data)
      setSugTotal(res.pagination.totalItems)
      // The queue refreshes itself when a scan has landed since it was last built. Say so,
      // rather than silently showing a different set of rows than last time.
      if (res.meta?.regenerated) {
        showSuccess('Suggestions Updated', 'Refreshed with brands found in the latest scan.')
      }
    } catch (e) {
      showError('Failed to Load Suggestions', e instanceof Error ? e.message : 'Please try again.')
    } finally {
      setSugLoading(false)
    }
  }, [customerId, sugTier])

  const regenerate = async () => {
    setGenerating(true)
    try {
      const res = await scoutCurationService.generateSuggestions(customerId)
      showSuccess('Suggestions Refreshed', res.message)
      await loadSuggestions()
    } catch (e) {
      showError('Refresh Failed', e instanceof Error ? e.message : 'Could not refresh suggestions.')
    } finally {
      setGenerating(false)
    }
  }

  const decide = async (sug: BrandSuggestion, action: 'accept' | 'reject', swap = false) => {
    setBusyId(sug.id)
    try {
      if (action === 'accept') {
        const res = await scoutCurationService.acceptSuggestion(sug.id, customerId, swap)
        showSuccess('Brands Merged', res.message)
      } else {
        await scoutCurationService.rejectSuggestion(sug.id, customerId)
        showSuccess('Kept Separate', `${sug.source_label} and ${sug.target_label} will stay as different brands.`)
      }
      setSuggestions(prev => prev.filter(s => s.id !== sug.id))
      setSugTotal(t => Math.max(0, t - 1))
    } catch (e) {
      showError('Action Failed', e instanceof Error ? e.message : 'Please try again.')
    } finally {
      setBusyId(null)
    }
  }

  const bulk = async (action: 'accept' | 'reject') => {
    if (!sugTier) return
    setGenerating(true)
    try {
      const res = await scoutCurationService.bulkReview(customerId, { action, tier: sugTier })
      showSuccess('Review Complete', res.message)
      await loadSuggestions()
    } catch (e) {
      showError('Bulk Action Failed', e instanceof Error ? e.message : 'Please try again.')
    } finally {
      setGenerating(false)
    }
  }

  const loadSelfCandidates = useCallback(async () => {
    if (!customerId) return
    try {
      const res = await scoutCurationService.getSelfCandidates(customerId)
      setSelfCandidates(res.data)
    } catch {
      // Non-fatal: the candidate list is a convenience, the per-brand toggle still works.
    }
  }, [customerId])

  const toggleSelf = async (brandId: string, label: string, next: boolean) => {
    setSelfBusyId(brandId)
    try {
      const res = await scoutCurationService.setIsSelf(brandId, next, customerId)
      showSuccess(next ? 'Marked as Your Brand' : 'Counts Towards Totals', res.message ||
        (next ? `${label} is excluded from totals.` : `${label} is included again.`))
      await Promise.all([loadBrands(), loadSelfCandidates()])
    } catch (e) {
      showError('Could Not Update Brand', e instanceof Error ? e.message : 'Please try again.')
    } finally {
      setSelfBusyId(null)
    }
  }

  const loadLog = useCallback(async () => {
    if (!customerId) return
    setLogLoading(true)
    try {
      const res = await scoutCurationService.getMergeLog(customerId, {
        origin: originFilter || undefined, limit: 50
      })
      setLog(res.data)
    } catch (e) {
      showError('Failed to Load History', e instanceof Error ? e.message : 'Please try again.')
    } finally {
      setLogLoading(false)
    }
  }, [customerId, originFilter])

  // Debounced so typing in the search box doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => { void loadBrands() }, 250)
    return () => clearTimeout(t)
  }, [loadBrands])

  useEffect(() => { if (tab === 'history') void loadLog() }, [tab, loadLog])
  useEffect(() => { if (tab === 'suggestions') void loadSuggestions() }, [tab, loadSuggestions])
  useEffect(() => { if (tab === 'brands') void loadSelfCandidates() }, [tab, loadSelfCandidates])

  useEffect(() => { setPage(1) }, [search, sortBy, sortOrder])

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(field)
      // Names read naturally A-Z; counts and dates are most useful largest-first.
      setSortOrder(field === 'label' ? 'asc' : 'desc')
    }
  }

  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE))

  const toggleSelect = (id: string) => {
    setPreview(null)
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      // Default the survivor to the first brand picked; the operator can move it.
      if (next.size === 1) setSurvivorId(Array.from(next)[0])
      if (next.size === 0) setSurvivorId(null)
      else if (survivorId && !next.has(survivorId)) setSurvivorId(Array.from(next)[0])
      return next
    })
  }

  const toggleExpand = async (brand: CurationBrand) => {
    if (expanded[brand.id]) {
      setExpanded(prev => { const n = { ...prev }; delete n[brand.id]; return n })
      return
    }
    setExpanded(prev => ({ ...prev, [brand.id]: 'loading' }))
    try {
      const res = await scoutCurationService.getMembers(brand.id, customerId)
      setExpanded(prev => ({ ...prev, [brand.id]: res.data.members }))
    } catch {
      setExpanded(prev => { const n = { ...prev }; delete n[brand.id]; return n })
    }
  }

  const runPreview = async () => {
    if (!survivorId || selected.size < 2) return
    setPreviewing(true)
    try {
      const sources = Array.from(selected).filter(id => id !== survivorId)
      const res = await scoutCurationService.previewMerge(survivorId, sources, customerId)
      setPreview(res.data)
    } catch (e) {
      showError('Preview Failed', e instanceof Error ? e.message : 'Please try again.')
    } finally {
      setPreviewing(false)
    }
  }

  const confirmMerge = async () => {
    if (!survivorId || selected.size < 2) return
    setMerging(true)
    try {
      const sources = Array.from(selected).filter(id => id !== survivorId)
      const res = await scoutCurationService.merge(survivorId, sources, customerId)
      showSuccess('Brands Merged', res.message)
      setSelected(new Set()); setSurvivorId(null); setPreview(null); setExpanded({})
      await loadBrands()
    } catch (e) {
      showError('Merge Failed', e instanceof Error ? e.message : 'Please try again.')
    } finally {
      setMerging(false)
    }
  }

  const undo = async (entry: MergeLogEntry) => {
    if (!entry.target_brand_id) return
    try {
      await scoutCurationService.unmerge(entry.target_brand_id, [entry.source_brand_id], customerId)
      showSuccess('Merge Undone', `${entry.source_name} is a separate brand again.`)
      await loadLog(); await loadBrands()
    } catch (e) {
      showError('Undo Failed', e instanceof Error ? e.message : 'Please try again.')
    }
  }

  const saveRename = async (brandId: string) => {
    try {
      await scoutCurationService.setDisplayName(brandId, renameValue.trim() || null, customerId)
      setRenamingId(null); setRenameValue('')
      await loadBrands()
    } catch (e) {
      showError('Rename Failed', e instanceof Error ? e.message : 'Please try again.')
    }
  }

  const survivorLabel = brands.find(b => b.id === survivorId)?.label ?? ''

  return (
    <div className="space-y-4">
      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {([
          ['suggestions', 'Suggestions', Lightbulb],
          ['brands', 'Brands', Layers],
          ['history', 'History', History]
        ] as const).map(([key, label, Icon]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === key
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Icon className="w-4 h-4" />{label}
            {key === 'suggestions' && sugTotal > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-amber-100 text-amber-800">
                {sugTotal}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === 'suggestions' && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-gray-600">Show:</span>
            {([['', 'All'], [2, 'Likely (tier 2)'], [3, 'Needs a look (tier 3)']] as const).map(([v, l]) => (
              <button
                key={String(v)}
                onClick={() => setSugTier(v as '' | 2 | 3)}
                className={`px-2.5 py-1 text-xs rounded-full border ${
                  sugTier === v ? 'bg-gray-900 text-white border-gray-900'
                                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
              >{l}</button>
            ))}
            <div className="flex-1" />
            {/* Bulk applies to a whole tier, so it is only offered once a tier is chosen —
                accepting "all suggestions" across both tiers is exactly the mistake tier 3
                exists to prevent. */}
            {sugTier !== '' && suggestions.length > 0 && (
              <>
                <Button size="sm" variant="secondary" onClick={() => bulk('accept')} disabled={generating}>
                  Accept all {suggestions.length}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => bulk('reject')} disabled={generating}>
                  Reject all
                </Button>
              </>
            )}
            <Button size="sm" variant="secondary" onClick={regenerate} disabled={generating}>
              {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <><RefreshCw className="w-3.5 h-3.5 mr-1" />Refresh</>}
            </Button>
          </div>

          {sugLoading && (
            <Card className="p-6 flex items-center justify-center text-gray-500 text-xs">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />Loading suggestions…
            </Card>
          )}

          {!sugLoading && suggestions.length === 0 && (
            <Card className="p-8 text-center">
              <Lightbulb className="w-6 h-6 mx-auto text-gray-300 mb-2" />
              <div className="text-sm text-gray-600">Nothing to review.</div>
              <div className="text-xs text-gray-400 mt-1">
                Refresh to look for new name variants after the next scan.
              </div>
            </Card>
          )}

          {!sugLoading && suggestions.map(sug => {
            const tier2 = sug.tier === 2
            return (
              <Card key={sug.id} className={`p-4 border-l-4 ${tier2 ? 'border-l-emerald-400' : 'border-l-amber-400'}`}>
                {/* The decision itself: which name survives, and in which direction. */}
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${avatarFor(sug.source_label)}`}>
                      {sug.source_label.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-medium text-gray-900 truncate">{sug.source_label}</div>
                      <div className="text-[10px] text-gray-500 flex items-center gap-2">
                        <span>{sug.source_posts} posts</span>
                        <PostLink url={sug.source_post_url} className="text-[10px]" />
                      </div>
                    </div>
                  </div>

                  <ArrowRight className="w-4 h-4 text-gray-400 shrink-0" />

                  <div className="flex items-center gap-2 min-w-0">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${avatarFor(sug.target_label)}`}>
                      {sug.target_label.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-medium text-gray-900 truncate">
                        {sug.target_label}
                        <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-blue-600 text-white">keeps</span>
                      </div>
                      <div className="text-[10px] text-gray-500 flex items-center gap-2">
                        <span>{sug.target_posts} posts</span>
                        <PostLink url={sug.target_post_url} className="text-[10px]" />
                      </div>
                    </div>
                  </div>

                  <div className="flex-1" />

                  <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded-full ${
                    tier2 ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-800'
                  }`}>
                    {tier2 ? 'likely' : 'check first'}
                  </span>
                </div>

                <div className="mt-2 text-[11px] text-gray-600">{sug.reason}</div>

                {/* Shared accounts is the strongest disambiguator on the card: two genuinely
                    different companies rarely show up on the same accounts. */}
                <div className="mt-2 flex flex-wrap items-center gap-3 text-[10px] text-gray-500">
                  <span className={sug.shared_accounts > 0 ? 'text-emerald-700 font-medium' : 'text-gray-400'}>
                    {sug.shared_accounts > 0
                      ? `seen together on ${sug.shared_accounts} account${sug.shared_accounts > 1 ? 's' : ''}`
                      : 'never seen on the same account'}
                  </span>
                  {sug.source_accounts.length > 0 && (
                    <span className="truncate">@{sug.source_accounts.slice(0, 3).join(', @')}</span>
                  )}
                </div>

                <div className="mt-3 flex items-center gap-2">
                  <Button size="sm" onClick={() => decide(sug, 'accept')} disabled={busyId === sug.id}>
                    {busyId === sug.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                       : <><Merge className="w-3.5 h-3.5 mr-1" />Merge</>}
                  </Button>
                  {/* Direction is frequently the only thing wrong with a suggestion. */}
                  <Button size="sm" variant="secondary" onClick={() => decide(sug, 'accept', true)} disabled={busyId === sug.id}>
                    <ArrowLeftRight className="w-3.5 h-3.5 mr-1" />Merge the other way
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => decide(sug, 'reject')} disabled={busyId === sug.id}>
                    Not the same
                  </Button>
                </div>
              </Card>
            )
          })}
        </>
      )}

      {tab === 'brands' && (
        <>
          <div className="flex items-center justify-between gap-3">
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search brands (matches merged names too)"
                className="pl-8 pr-8 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-72"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label="Clear search"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <div className="text-xs text-gray-500">
              Tick two or more brands to merge them.
            </div>
          </div>

          {/* Own brands: a customer's own brand dominates their own results, so it is kept out
              of the totals. Proposed here rather than applied, because a name match alone
              cannot tell "Harvard" the customer from "Harvard Business Review" the publisher. */}
          {selfCandidates.length > 0 && (
            <Card className="p-3 border-l-4 border-l-violet-400 bg-violet-50/40">
              <div className="flex flex-wrap items-center gap-2">
                <Home className="w-4 h-4 text-violet-700" />
                <span className="text-xs text-gray-700">
                  <strong>{selfCandidates.length}</strong> brand{selfCandidates.length > 1 ? 's' : ''} look like
                  {' '}{selfCandidates[0]?.customer_name ? <strong>{selfCandidates[0].customer_name}</strong> : 'your own'}.
                  Marking them keeps them out of your totals.
                </span>
                <div className="flex-1" />
                <Button size="sm" variant="secondary" onClick={() => setShowCandidates(v => !v)}>
                  {showCandidates ? 'Hide' : 'Review'}
                </Button>
              </div>

              {showCandidates && (
                <div className="mt-3 pt-3 border-t border-violet-200 space-y-1">
                  {selfCandidates.map(c => (
                    <div key={c.id} className="flex items-center gap-2 text-xs">
                      <span className="font-medium text-gray-900">{c.label}</span>
                      <span className="text-[10px] text-gray-500">{c.posts} posts</span>
                      <span className={`px-1.5 py-0.5 text-[10px] rounded-full ${
                        c.match_kind === 'exact' ? 'bg-violet-100 text-violet-800'
                        : c.match_kind === 'own_account_handle' ? 'bg-blue-50 text-blue-700'
                        : 'bg-gray-100 text-gray-600'
                      }`}>
                        {c.match_kind === 'exact' ? 'matches your name'
                         : c.match_kind === 'own_account_handle' ? 'one of your accounts'
                         : 'starts with your name'}
                      </span>
                      <div className="flex-1" />
                      <Button size="sm" onClick={() => toggleSelf(c.id, c.label, true)} disabled={selfBusyId === c.id}>
                        {selfBusyId === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'This is ours'}
                      </Button>
                    </div>
                  ))}
                  <p className="text-[10px] text-gray-500 pt-1">
                    Not all of these are yours — a brand can share your name without being you.
                  </p>
                </div>
              )}
            </Card>
          )}

          {/* Merge bar — only once two or more brands are picked */}
          {selected.size >= 2 && (
            <Card className="p-3 border-blue-300 bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="flex flex-wrap items-center gap-2">
                <Merge className="w-4 h-4 text-blue-700" />
                <span className="text-xs text-gray-700">
                  Merging <strong>{selected.size}</strong> brands. Keep:
                </span>
                <select
                  value={survivorId ?? ''}
                  onChange={e => { setSurvivorId(e.target.value); setPreview(null) }}
                  className="border border-gray-300 rounded-lg px-2 py-1 text-xs focus:ring-2 focus:ring-blue-500"
                >
                  {Array.from(selected).map(id => (
                    <option key={id} value={id}>{brands.find(b => b.id === id)?.label ?? id}</option>
                  ))}
                </select>
                <div className="flex-1" />
                <Button variant="secondary" onClick={runPreview} disabled={previewing}>
                  {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Preview'}
                </Button>
                <Button onClick={confirmMerge} disabled={merging}>
                  {merging ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Merge'}
                </Button>
                <Button variant="ghost" onClick={() => { setSelected(new Set()); setSurvivorId(null); setPreview(null) }}>
                  Cancel
                </Button>
              </div>

              {preview && (
                <div className="mt-3 pt-3 border-t border-blue-200 space-y-2">
                  <div className="flex flex-wrap gap-4">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-gray-500">Posts after merge</div>
                      <div className="text-sm font-semibold text-gray-900">{preview.combined_posts.toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-gray-500">EMV after merge</div>
                      <div className="text-sm font-semibold text-emerald-700">{fmtMoney(preview.combined_emv)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-gray-500">Shared posts</div>
                      <div className="text-sm font-semibold text-gray-900">{preview.shared_posts.toLocaleString()}</div>
                    </div>
                    <div className="border-l border-blue-200 pl-4">
                      <div className="text-[10px] uppercase tracking-wider text-gray-500">Keeps the name</div>
                      <div className="text-sm font-semibold text-gray-900">{survivorLabel}</div>
                    </div>
                  </div>
                  {/* Stated, not implied: the parts genuinely do not add up, and a reviewer who
                      does the arithmetic should find the explanation here rather than a bug. */}
                  {preview.overlapNote && (
                    <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                      {preview.overlapNote}
                    </div>
                  )}
                </div>
              )}
            </Card>
          )}

          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="border-b border-gray-200 bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 w-10" />
                    <th className="px-3 py-2 w-8" />
                    <th
                      className="px-3 py-2 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                      onClick={() => handleSort('label')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Brand</span>
                        <ChevronDown className={`h-3 w-3 transition-transform ${
                          sortBy === 'label' ? (sortOrder === 'desc' ? 'rotate-0' : 'rotate-180') : 'opacity-30'
                        }`} />
                      </div>
                    </th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                      Category
                    </th>
                    <th
                      className="px-3 py-2 text-right text-[10px] font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                      onClick={() => handleSort('emv')}
                    >
                      <div className="flex items-center justify-end space-x-1">
                        <span>EMV</span>
                        <ChevronDown className={`h-3 w-3 transition-transform ${
                          sortBy === 'emv' ? (sortOrder === 'desc' ? 'rotate-0' : 'rotate-180') : 'opacity-30'
                        }`} />
                      </div>
                    </th>
                    <th
                      className="px-3 py-2 text-right text-[10px] font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                      onClick={() => handleSort('posts')}
                    >
                      <div className="flex items-center justify-end space-x-1">
                        <span>Posts</span>
                        <ChevronDown className={`h-3 w-3 transition-transform ${
                          sortBy === 'posts' ? (sortOrder === 'desc' ? 'rotate-0' : 'rotate-180') : 'opacity-30'
                        }`} />
                      </div>
                    </th>
                    <th
                      className="px-3 py-2 text-right text-[10px] font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                      onClick={() => handleSort('detections')}
                    >
                      <div className="flex items-center justify-end space-x-1">
                        <span>Detections</span>
                        <ChevronDown className={`h-3 w-3 transition-transform ${
                          sortBy === 'detections' ? (sortOrder === 'desc' ? 'rotate-0' : 'rotate-180') : 'opacity-30'
                        }`} />
                      </div>
                    </th>
                    <th
                      className="px-3 py-2 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                      onClick={() => handleSort('last_seen_at')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Last Seen</span>
                        <ChevronDown className={`h-3 w-3 transition-transform ${
                          sortBy === 'last_seen_at' ? (sortOrder === 'desc' ? 'rotate-0' : 'rotate-180') : 'opacity-30'
                        }`} />
                      </div>
                    </th>
                    <th className="px-3 py-2 text-right text-[10px] font-semibold text-gray-600 uppercase tracking-wider w-28">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {loading && (
                    <tr><td colSpan={9} className="px-3 py-8">
                      <div className="flex items-center justify-center text-gray-500 text-xs">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />Loading brands…
                      </div>
                    </td></tr>
                  )}
                  {!loading && brands.length === 0 && (
                    <tr><td colSpan={9} className="px-3 py-8 text-center text-gray-500 text-xs">
                      No brands found.
                    </td></tr>
                  )}
                  {!loading && brands.map(b => {
                    const isSelected = selected.has(b.id)
                    const isSurvivor = survivorId === b.id && selected.size >= 2
                    return (
                      <React.Fragment key={b.id}>
                        <tr className={`transition-colors ${
                          isSurvivor ? 'bg-blue-50' : isSelected ? 'bg-blue-50/40' : 'hover:bg-gray-50'
                        }`}>
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleSelect(b.id)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              aria-label={`Select ${b.label}`}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <button
                              onClick={() => toggleExpand(b)}
                              disabled={b.member_count === 0}
                              className={b.member_count === 0 ? 'invisible' : 'text-gray-400 hover:text-gray-700'}
                              aria-label="Show merged names"
                            >
                              {expanded[b.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                            </button>
                          </td>
                          <td className="px-3 py-2">
                            {renamingId === b.id ? (
                              <div className="flex items-center gap-2">
                                <input
                                  value={renameValue}
                                  onChange={e => setRenameValue(e.target.value)}
                                  onKeyDown={e => { if (e.key === 'Enter') void saveRename(b.id) }}
                                  autoFocus
                                  className="px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent w-48"
                                />
                                <Button size="sm" onClick={() => saveRename(b.id)}>Save</Button>
                                <Button size="sm" variant="ghost" onClick={() => setRenamingId(null)}>Cancel</Button>
                                {/* brand_name is the scan pipeline's match key and never changes. */}
                                <span className="text-[10px] text-gray-400 whitespace-nowrap">detected as “{b.brand_name}”</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 min-w-0">
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${avatarFor(b.brand_name)}`}>
                                  {b.label.charAt(0).toUpperCase()}
                                </div>
                                <span className="text-xs font-medium text-gray-900 truncate">{b.label}</span>
                                {b.member_count > 0 && (
                                  <span className="px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-indigo-50 text-indigo-700 whitespace-nowrap">
                                    {b.member_count} merged
                                  </span>
                                )}
                                {b.display_name && (
                                  <span className="px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-gray-100 text-gray-600">
                                    renamed
                                  </span>
                                )}
                                {b.is_self && (
                                  <span className="px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-violet-100 text-violet-800 whitespace-nowrap">
                                    your brand
                                  </span>
                                )}
                                {isSurvivor && (
                                  <span className="px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-blue-600 text-white whitespace-nowrap">
                                    keeps
                                  </span>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <span className="text-[10px] text-gray-500">{b.category || '—'}</span>
                          </td>
                          <td className="px-3 py-2 text-right">
                            <span className="text-xs font-semibold text-emerald-700">{fmtEMV(b.total_emv)}</span>
                          </td>
                          <td className="px-3 py-2 text-right">
                            <span className="text-xs font-semibold text-gray-900">{b.posts.toLocaleString()}</span>
                          </td>
                          <td className="px-3 py-2 text-right">
                            <span className="text-xs text-gray-500">{b.detections.toLocaleString()}</span>
                          </td>
                          <td className="px-3 py-2">
                            <span className="text-[10px] text-gray-500 whitespace-nowrap">{fmtDate(b.last_seen_at)}</span>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-medium">
                            <div className="flex items-center justify-end space-x-0.5">
                              <div className="relative group">
                                <Button variant="ghost" size="sm"
                                  onClick={(e) => { e.stopPropagation(); setRenamingId(b.id); setRenameValue(b.display_name ?? b.brand_name) }}
                                  aria-label={`Rename ${b.label}`}
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                                <div className="absolute bottom-full right-0 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                                  Rename
                                </div>
                              </div>

                              <div className="relative group">
                                {b.post_url ? (
                                  <a href={b.post_url} target="_blank" rel="noopener noreferrer"
                                     onClick={(e) => e.stopPropagation()}
                                     className="inline-flex items-center justify-center px-2 py-1 rounded text-gray-500 hover:text-blue-600 hover:bg-gray-100"
                                     aria-label={`View a post featuring ${b.label}`}>
                                    <ExternalLink className="h-3.5 w-3.5" />
                                  </a>
                                ) : (
                                  <span className="inline-flex items-center justify-center px-2 py-1 text-gray-200" aria-hidden="true">
                                    <ExternalLink className="h-3.5 w-3.5" />
                                  </span>
                                )}
                                <div className="absolute bottom-full right-0 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                                  {b.post_url ? 'View a post with this brand' : 'No post recorded'}
                                </div>
                              </div>

                              <div className="relative group">
                                <Button variant="ghost" size="sm"
                                  onClick={(e) => { e.stopPropagation(); toggleSelf(b.id, b.label, !b.is_self) }}
                                  disabled={selfBusyId === b.id}
                                  aria-label={b.is_self ? `Unmark ${b.label} as your brand` : `Mark ${b.label} as your brand`}
                                >
                                  {selfBusyId === b.id
                                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    : <Home className={`h-3.5 w-3.5 ${b.is_self ? 'text-violet-600' : ''}`} />}
                                </Button>
                                <div className="absolute bottom-full right-0 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                                  {b.is_self ? 'Count towards totals again' : 'Mark as our own brand'}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>

                        {expanded[b.id] && (
                          <tr className="bg-gray-50/70">
                            <td colSpan={9} className="px-3 py-2">
                              <div className="pl-12 space-y-1">
                                {expanded[b.id] === 'loading' ? (
                                  <div className="text-[10px] text-gray-500 flex items-center gap-2">
                                    <Loader2 className="w-3 h-3 animate-spin" />Loading merged names…
                                  </div>
                                ) : (expanded[b.id] as BrandMember[]).map(m => (
                                  <div key={m.id} className="flex items-center gap-2 text-xs">
                                    <span className={m.is_canonical ? 'font-medium text-gray-900' : 'text-gray-600'}>
                                      {m.label}
                                    </span>
                                    {m.is_canonical ? (
                                      <span className="px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-emerald-50 text-emerald-700">
                                        canonical
                                      </span>
                                    ) : (
                                      <span className="px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-gray-100 text-gray-600">
                                        merged {fmtDate(m.merged_at)}
                                      </span>
                                    )}
                                    <span className="text-[10px] text-gray-400">
                                      {m.posts} posts · {m.detections} detections
                                    </span>
                                    <PostLink url={m.post_url} className="text-[10px]" />
                                    <div className="flex-1" />
                                    {!m.is_canonical && (
                                      <button
                                        onClick={() => scoutCurationService
                                          .unmerge(b.id, [m.id], customerId)
                                          .then(() => { setExpanded({}); return loadBrands() })
                                          .then(() => showSuccess('Brand Released', `${m.label} is a separate brand again.`))
                                          .catch(e => showError('Release Failed', e.message))}
                                        className="text-[10px] text-blue-600 hover:underline flex items-center gap-1"
                                      >
                                        <Undo2 className="w-3 h-3" />Release
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Results summary + pagination, matching PostsTable */}
          {totalItems > 0 && (
            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500">
                  Showing {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, totalItems)} of {totalItems} brands
                </div>
                {totalPages > 1 && (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setPage(1)}
                      disabled={page === 1 || loading}
                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors shadow-sm focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                    >First</button>
                    <button
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1 || loading}
                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors shadow-sm focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                    >Previous</button>
                    <span className="text-xs text-gray-600">Page {page} of {totalPages}</span>
                    <button
                      onClick={() => setPage(page + 1)}
                      disabled={page >= totalPages || loading}
                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors shadow-sm focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                    >Next</button>
                    <button
                      onClick={() => setPage(totalPages)}
                      disabled={page >= totalPages || loading}
                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors shadow-sm focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                    >Last</button>
                  </div>
                )}
              </div>
            </Card>
          )}

        </>
      )}

      {tab === 'history' && (
        <>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Show:</span>
            {([['', 'All'], ['auto', 'Automatic'], ['support', 'Support'], ['customer', 'Customer']] as const).map(([v, l]) => (
              <button
                key={v}
                onClick={() => setOriginFilter(v)}
                className={`px-2.5 py-1 text-xs rounded-full border ${
                  originFilter === v ? 'bg-gray-900 text-white border-gray-900'
                                     : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
              >{l}</button>
            ))}
          </div>

          <Card className="divide-y divide-gray-100">
            {logLoading && (
              <div className="p-6 flex items-center justify-center text-gray-500">
                <Loader2 className="w-5 h-5 animate-spin mr-2" />Loading history…
              </div>
            )}
            {!logLoading && log.length === 0 && (
              <div className="p-6 text-center text-gray-500 text-sm">
                Nothing has been merged or renamed yet.
              </div>
            )}
            {!logLoading && log.map(e => (
              <div key={e.id} className="flex items-center gap-3 px-4 py-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-gray-900">
                    {e.action === 'merge' ? (
                      <>Merged <strong>{e.source_name}</strong> into <strong>{e.target_name}</strong></>
                    ) : (
                      <>Released <strong>{e.source_name}</strong></>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
                    <Badge variant={ORIGIN_VARIANT[e.origin] ?? 'default'}>
                      {e.origin === 'auto' ? 'automatic' : e.origin}
                    </Badge>
                    {e.rule && <span>rule: {e.rule}</span>}
                    <span>{fmtDate(e.created_at)}</span>
                    {e.note && <span className="italic truncate">“{e.note}”</span>}
                  </div>
                </div>
                {e.action === 'merge' && e.still_merged && (
                  <Button size="sm" variant="secondary" onClick={() => undo(e)}>
                    <Undo2 className="w-3.5 h-3.5 mr-1" />Undo
                  </Button>
                )}
              </div>
            ))}
          </Card>

          {context === 'customer' && (
            <p className="text-xs text-gray-500">
              Merges marked <strong>automatic</strong> were applied by the system for names that
              differ only in spelling or punctuation. Undo any of them if they are wrong.
            </p>
          )}
        </>
      )}
    </div>
  )
}

export default BrandCurationPanel
